import { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import moment from 'moment';
import { isEmpty } from 'lodash';
import { isValidInterestRate } from '../utils';

import { formatDateDisplayLocal } from '@fortress-technology-solutions/fortress-component-library/utils';

import SecurityDepositInterestService from '../../../../services/securityDepositInterest';

const sdiService = new SecurityDepositInterestService();
export const useSecurityDepositInterestRates = ({
  organizationId,
  propertyId,
}) => {
  const {
    securityDepositInterestRates,
    setSecurityDepositInterestRates,
    fetchingRates,
  } = useFetchSecurityDepositInterestRates({
    organizationId,
    propertyId,
  });
  const editableRates = securityDepositInterestRates.filter(
    (rate) => rate.startDateIsEditable || rate.endDateIsEditable,
  );
  const readOnlyRates = securityDepositInterestRates.filter(
    (rate) => !rate.startDateIsEditable && !rate.endDateIsEditable,
  );
  const [newInterestRatesToCreate, setNewInterestRatesToCreate] = useState([]);
  const [modifiedRates, setModifiedRates] = useState([]);

  function resetModifications() {
    setModifiedRates([]);
    setNewInterestRatesToCreate([]);
  }

  const { updateSecurityDepositInterestRates, updatingRates } =
    useUpdateSecurityDepositInterestRates({
      organizationId,
      propertyId,
      resetModifications,
    });

  const handleSubmit = () => {
    let ratesToUpdate = [];

    if (modifiedRates.length > 0) {
      ratesToUpdate = [...modifiedRates];
    }
    return updateSecurityDepositInterestRates({
      newRates: newInterestRatesToCreate,
      modifiedRates: ratesToUpdate,
    });
  };

  const interestRatesAreValid = useMemo(() => {
    return (
      securityDepositInterestRates.every((rate) => {
        return (
          (rate.startDate &&
            isValidInterestRate(rate.interest) &&
            rate.interest <= 10 &&
            rate.interest >= 0) ||
          (!rate.startDate &&
            !rate.endDate &&
            !isValidInterestRate(rate.interest))
        );
      }) &&
      newInterestRatesToCreate.every((rate) => {
        return (
          (rate.startDate &&
            isValidInterestRate(rate.interest) &&
            rate.interest <= 10 &&
            rate.interest >= 0) ||
          (!rate.startDate &&
            !rate.endDate &&
            !isValidInterestRate(rate.interest))
        );
      })
    );
  }, [securityDepositInterestRates, newInterestRatesToCreate]);

  return {
    securityDepositInterestRatesIsLoading: fetchingRates || updatingRates,
    securityDepositInterestRates,
    setSecurityDepositInterestRates,
    editableRates,
    readOnlyRates,
    updateSecurityDepositInterestRates: handleSubmit,
    modifiedRates,
    setModifiedRates,
    newInterestRatesToCreate,
    setNewInterestRatesToCreate,
    interestRatesAreValid,
  };
};

export const useFetchSecurityDepositInterestRates = ({
  organizationId,
  propertyId,
}) => {
  const [securityDepositInterestRates, setSecurityDepositInterestRates] =
    useState([]);
  const queryKey = ['securityDepositInterestRates', organizationId, propertyId];
  const { data, isLoading } = useQuery(
    queryKey,
    async () => {
      return sdiService.getSecurityDepositInterestRates({
        organizationId,
        propertyId,
      });
    },
    {
      enabled: !!propertyId,
      refetchOnWindowFocus: false,
      initialData: [],
    },
  );

  useEffect(() => {
    const mappedRates = data.map(mapInterestRate);
    setSecurityDepositInterestRates(mappedRates);
  }, [data, isLoading]);

  return {
    securityDepositInterestRates,
    setSecurityDepositInterestRates,
    fetchingRates: isLoading,
  };
};

function mapInterestRate(rate) {
  const startDateIsEditable = moment(rate.startDate).isAfter(
    moment().startOf('month'),
  );
  const endDateIsEditable =
    rate.endDate === null ||
    moment(rate.endDate).isAfter(moment().startOf('month'));
  const endDate = (() => {
    if (rate.endDate === null) {
      return null;
    }
    if (endDateIsEditable) {
      return moment(rate.endDate);
    } else {
      return rate.endDate;
    }
  })();

  return {
    ...rate,
    interest: rate.interestRate,
    startDate: startDateIsEditable ? moment(rate.startDate) : rate.startDate,
    endDate: endDate,
    startDateIsEditable,
    endDateIsEditable,
    plainStartDate: rate.startDate,
    plainEndDate: rate.endDate,
  };
}

export const useUpdateSecurityDepositInterestRates = ({
  organizationId,
  propertyId,
  resetModifications,
}) => {
  const queryClient = useQueryClient();
  const queryKey = ['securityDepositInterestRates', organizationId, propertyId];
  const bulkCreateSecurityDepositInterestRatesMutation = useMutation(
    async ({ newRates, modifiedRates }) => {
      const ratesToCreate = newRates
        .filter((r) => {
          // Don't create empty rates
          return !(
            isEmpty(r.startDate) &&
            isEmpty(r.endDate) &&
            !isValidInterestRate(r.interest)
          );
        })
        .map((rate) => ({
          startDate: formatDateDisplayLocal(rate.startDate),
          endDate:
            (rate.endDate && formatDateDisplayLocal(rate.endDate)) || null,
          interest: rate.interest,
        }));

      const modifiedRatesToUpdate = modifiedRates.map((rate) => {
        const updatePayload = {
          id: rate.id,
        };
        if (rate.startDate) {
          updatePayload.startDate = formatDateDisplayLocal(rate.startDate);
        }
        if (rate.endDate) {
          updatePayload.endDate = formatDateDisplayLocal(rate.endDate);
        }
        if (isValidInterestRate(rate.interest)) {
          updatePayload.interest = rate.interest;
        }
        return updatePayload;
      });
      if (ratesToCreate.length > 0 || modifiedRatesToUpdate.length > 0) {
        return sdiService.updateSecurityDepositInterestRates(
          organizationId,
          propertyId,
          {
            newRates: ratesToCreate,
            modifiedRates: modifiedRatesToUpdate,
          },
        );
      }
      return true;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(queryKey);
        resetModifications();
      },
    },
  );

  return {
    updateSecurityDepositInterestRates:
      bulkCreateSecurityDepositInterestRatesMutation.mutate,
    updatingRates: bulkCreateSecurityDepositInterestRatesMutation.isLoading,
  };
};
